import React, { useState, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'store/ducks';

import { Row, Column } from '../Layout/Grid';
import Text from '../Text';
import { ITableSearchOpenProtocol } from './interface';
import { Button } from '../Button';
import {
  addKeyPressListener,
  removeKeyPressListener,
} from 'utils/searchWithEnterPress';
import { DateOutputStr } from 'utils/DateOutput';

import IconSearch from 'img/search-icon.svg';

import DomainSettingService from 'services/domainSetting.service';

import { IDomainSetting } from 'models/DomainSetting';
import { Capitalize } from 'utils/String';

import './style.scss';
import PeriodDateToInput from 'components/PeriodDateToInput';

const TableSearchOPenProtocol: React.FC<ITableSearchOpenProtocol> = (props) => {
  const {
    inputFilter,
    fastFilters,
    statusFilters,
    setTableFilter,
    dateKey,
    tableOrderBy,
    setTableOrderBy,
    className,
    showSituationFilter,
    setCurrentPage,
    dateMin,
    dateMax,
    showGroupSelect,
    ShowStatusSelect,
    clearBtn,
    statusLabel,
  } = props;

  const randomTableSearchId = `${Math.random() * 1000}`;
  const [searchStr, setSearchStr] = useState('');
  const [period, setPeriod] = useState({
    from: dateMin ? DateOutputStr(dateMin.toISOString(), 'yyyy-MM-dd') : '',
    to: dateMax ? DateOutputStr(dateMax.toISOString(), 'yyyy-MM-dd') : '',
  });
  const [selectedOption, setSelectedOption] = useState(JSON.stringify({}));
  const [situationSelectedOption, setSituationSelectedOption] = useState('');

  const searchStrRef = useRef(searchStr);
  const periodRef = useRef(period);
  const selectedOptionRef = useRef(selectedOption);
  const situationSelectedOptionRef = useRef(situationSelectedOption);

  const _domainSettingService = new DomainSettingService();
  const [userProfileTypes, setUserProfileTypes] = useState(
    Array<IDomainSetting>()
  );
  const [trackingTypes, setTrackingProfileTypes] = useState(
    Array<IDomainSetting>()
  );
  const [profileInput, setProfileInput] = useState<string | undefined>('0');
  const user = useSelector((state: RootState) => state.user);

  function makeFilter() {
    const searchStrFilter = searchStrRef.current;
    const periodCurrent = periodRef.current;
    const selectedOptionFilter = selectedOptionRef.current;
    const situationSelectedOptionFilter = situationSelectedOptionRef.current;
    /**
     * First format
     */
    const model = {
      searchStrFilter,
      period: {
        start: periodCurrent.from ? `${periodCurrent.from}T00:00:00.000Z` : '',
        end: periodCurrent.to ? `${periodCurrent.to}T23:59:59.999Z` : '',
      },
      selectedOptionFilter: JSON.parse(selectedOptionFilter),
    };

    const filterData: any = {
      and: {},
    };

    if (model.searchStrFilter) {
      const query = inputFilter?.filters(model.searchStrFilter.toLowerCase());
      if (query.length > 0) filterData.and = { ...filterData.and, or: query };
    } else {
      inputFilter?.filters(model.searchStrFilter.toLowerCase());
    }

    /**
     * Date OData format
     */
    if (dateKey) {
      if (periodCurrent.from && periodCurrent.to) {
        filterData.and = {
          ...filterData.and,
          and: [
            { [dateKey]: { ge: new Date(model.period.start) } },
            { [dateKey]: { le: new Date(model.period.end) } },
          ],
        };
      } else if (periodCurrent.from && !periodCurrent.to) {
        filterData.and = {
          ...filterData.and,
          [dateKey]: { ge: new Date(model.period.start) },
        };
      } else if (!periodCurrent.from && periodCurrent.to) {
        filterData.and = {
          ...filterData.and,
          [dateKey]: { le: new Date(model.period.end) },
        };
      }

      /**
       * Select option format
       */
      if (Object.entries(model.selectedOptionFilter).length) {
        filterData.and = { ...filterData.and, ...model.selectedOptionFilter };
      }

      if (situationSelectedOptionFilter) {
        if (situationSelectedOptionFilter === 'Abertos') {
          filterData.and = {
            ...filterData.and,
            statusType: { ne: 'DONE' }
          };
        } else if (situationSelectedOptionFilter === 'Concluidos') {
          filterData.and = {
            ...filterData.and,
            statusType: { eq: 'DONE' }
          };
        }
      }
    }

    if (showGroupSelect) {
      if (
        profileInput !== undefined &&
        profileInput !== '' &&
        profileInput !== '0'
      ) {
        filterData.and = {
          ...filterData.and,
          responsibleGroup: { eq: profileInput },
        };
      }
    }

    setCurrentPage(0);
    setTableFilter((filters: any) => ({ ...filters, ...filterData }));
  }

  function makeFilterOneGroup(filterUser: string) {
    const filterData: any = {
      and: {},
    };

    if (showGroupSelect) {
      if (filterUser) {
        filterData.and = {
          ...filterData.and,
          responsibleGroup: { eq: filterUser },
        };
      }
    }

    setCurrentPage(0);
    setTableFilter((filters: any) => ({ ...filters, ...filterData }));
  }

  useEffect(() => {
    if (user?.profileType?.length === 1 && user.profileType[0] !== 'CUSTOMER') {
      setProfileInput(user.profileType[0]);
      makeFilterOneGroup(user.profileType[0]);
    }
  }, [user]);

  /**
   * Set table order with value of clicked fastFilter
   * @param value filter value
   */
  function setFastFilter(value: any) {
    if (tableOrderBy === value) return;

    setTableOrderBy(value);
  }

  /**
   * Only returns a filter style
   * @param filter
   */
  const fastSearchStyles = (filter: any) =>
    `${tableOrderBy === filter.value && 'border-bottom-2'} font-bold ${tableOrderBy === filter.value ? 'color-primary' : 'color-dark-gray'
    }`;

  useEffect(() => {
    searchStrRef.current = searchStr;
  }, [searchStr]);

  useEffect(() => {
    periodRef.current = period;
  }, [period]);

  useEffect(() => {
    selectedOptionRef.current = selectedOption;
  }, [selectedOption]);

  useEffect(() => {
    situationSelectedOptionRef.current = situationSelectedOption;
  }, [situationSelectedOption]);

  useEffect(() => {
    addKeyPressListener(randomTableSearchId, makeFilter);

    return () => {
      removeKeyPressListener(randomTableSearchId, makeFilter);
    };
  }, []);

  useEffect(() => {
    _domainSettingService
      .get(undefined, undefined, 'value asc', {
        and: [
          { identifier: { eq: 'UserProfileTypes' } },
          { enabled: { eq: true } },
        ],
      })
      .then((result: any) => {
        if (result && result.data) {
          const _userProfileTypes: Array<IDomainSetting> = result.data;

          setUserProfileTypes(_userProfileTypes);
        }
      });
  }, []);

  useEffect(() => {
    _domainSettingService
      .get(undefined, undefined, 'value asc', {
        and: [
          { identifier: { eq: 'TrackingTypes' } },
          { enabled: { eq: true } },
        ],
      })
      .then((result: any) => {
        if (result && result.data) {
          const _trackingTypes: Array<IDomainSetting> = result.data;

          setTrackingProfileTypes(_trackingTypes);
        }
      });
  }, []);

  const clearFilter = () => {
    const filterData: any = {
      and: {},
    };

    setSearchStr('');
    setProfileInput('');

    setCurrentPage(0);
    setTableFilter((filters: any) => ({ ...filters, ...filterData }));
  };

  return (
    <Row
      id={randomTableSearchId}
      tabIndex={0}
      className={`mt-4 align-center flex-md-column align-md-none flex-wrap justify-md-none bg-white ${className || 'px-3 py-3'
        }`}
      style={{ borderRadius: '10px 10px 0px 0px' }}
    >
      <Column className="mr-2 mr-md-0 flex-1 w-md-100">
        <Text className="mb-1 color-blue-gray" size={12} weight="bold">
          Filtro
        </Text>
        <Row className="flex-1">
          <input
            onChange={(e) => setSearchStr(e.target.value)}
            type="text"
            className="flex-1"
            placeholder={inputFilter?.placeholder || ''}
            value={searchStr}
          />
        </Row>
      </Column>
      {dateKey && (
        <Column className="flex-1 mr-2 mr-md-0 w-md-100 mt-md-3">
          <Text className="mr-2 mb-1 color-blue-gray" size={12} weight="bold">
            Período
          </Text>
          <Row className="flex-1 align-center">
            <input
              className="flex-1"
              value={period.from}
              onChange={(e) => setPeriod({ ...period, from: e.target.value })}
              type="date"
              max={period.to}
              placeholder="Início"
            />
            <Text className="mx-2 color-blue-gray" size={12} weight="bold">
              a
            </Text>
            <PeriodDateToInput
              className="flex-1"
              period={period}
              setPeriod={setPeriod}
              placeHolder={"Fim"}
            />
          </Row>
        </Column>
      )}
      {statusFilters && (
        <Column className="mr-2 mr-lg-0 mt-md-3 flex-1 w-md-100">
          <Text className="mr-2 mb-1 color-blue-gray" size={12} weight="bold">
            Status
          </Text>
          <Row className="flex-1">
            <select
              value={selectedOption}
              onChange={(e) => setSelectedOption(e.target.value)}
              className="flex-1"
            >
              <option value={JSON.stringify({})}>Todos</option>
              {statusFilters.map((filter, index) => (
                <option
                  key={index}
                  label={filter.label}
                  value={JSON.stringify(filter.value)}
                />
              ))}
            </select>
          </Row>
        </Column>
      )}
      {showSituationFilter && (
        <Column className="mr-2 mr-lg-0 mt-md-3 flex-1 w-md-100">
          <Text className="mr-2 mb-1 color-blue-gray" size={12} weight="bold">
            Situação
          </Text>
          <Row className="flex-1">
            <select
              value={situationSelectedOption}
              onChange={(e) => setSituationSelectedOption(e.target.value)}
              className="flex-1"
            >
              <option value="">Todos</option>
              {trackingTypes.map((filter, index) => (
                <option
                  key={index}
                  label={Capitalize(filter.value)}
                  value={filter.value}
                />
              ))}
            </select>
          </Row>
        </Column>
      )}
      {showGroupSelect && (
        <Column className="mr-2">
          <Text size={12} className="mb-1 color-blue-gray mb-lg-1 mr-2">
            Grupo Responsável
          </Text>
          <Row className="align-center flex-sm-column">
            <select
              name="profile"
              defaultValue={profileInput}
              onChange={(e) => setProfileInput(e.target.value)}
              value={profileInput}
              className="w-lg-100 mb-sm-3"
              style={{ minWidth: 210 }}
            >
              <option value="">Todos</option>
              {userProfileTypes.map((filter, index) => {
                if (filter.key !== 'CUSTOMER') {
                  return <option key={index} label={filter.value} value={filter.key} />;
                }
              })}
            </select>
          </Row>
        </Column>
      )}
      {ShowStatusSelect && (
        <Column className="mr-2">
          <Text size={12} className="mb-1 color-blue-gray mb-lg-1 mr-2">
            {statusLabel}
          </Text>
          <Row className="align-center flex-sm-column w-sm-100">
            <select
              style={{ width: 150 }}
              onChange={(e) => setSituationSelectedOption(e.target.value)}
              className="w-sm-100">
              <option key="0" label="Todos" value="Todos">Todos</option>
              <option key="1" label="Abertos" value="Abertos">Abertos</option>
              <option key="2" label="Concluidos" value="Concluidos">Concluídos</option>
            </select>
          </Row>
        </Column>
      )}
      <Column className="mr-2 mr-lg-0 mt-md-3 w-md-100 pt-3 pt-md-0">
        {/* <Row className="mb-1" style={{ height: 15 }} /> */}
        <Row>
          <Button
            tabIndex={0}
            className="focus-button"
            width="auto"
            onClick={makeFilter}
          >
            <img src={IconSearch} alt="" />
          </Button>
        </Row>
      </Column>
      {fastFilters && (
        <Row className="mt-3 mt-md-3 align-center justify-between flex-1 flex-lg-basis-100 w-md-100 flex-md-column">
          <Row className="align-center flex-1 justify-start justify-md-center w-md-100 align-md-none">
            {fastFilters.map((filter, index) => (
              <Button
                key={index}
                className={`${index > 0 && 'ml-0 ml-md-0'}`}
                onClick={() => setFastFilter(filter.value)}
                width="auto"
              >
                <Text
                  size={12}
                  className={`border-primary ${fastSearchStyles(
                    filter
                  )} pb-1 px-2`}
                >
                  {filter.label}
                </Text>
              </Button>
            ))}
          </Row>
        </Row>
      )}
      {clearBtn && (
        <Row>
          <Button onClick={() => clearFilter()}>
            <Text size={14} className="color-primary mt-2">
              x Limpar filtros
            </Text>
          </Button>
        </Row>
      )}
    </Row>
  );
};

export default TableSearchOPenProtocol;
